import { getFrontendConfigValue, MenuItems, theme, UserMenuDropdown } from '@arnold/common';
import styled from '@emotion/styled/macro';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Modals } from '../chat/ChatWindow';
import {
  ContactType,
  GetChannelQuery,
  IsAccessTokenValidQuery,
  ReportSubscriptionSubscription,
  UserOrgRole,
  UserSysRole,
} from '../generated/hooks';
import auth from '../lib/auth';
import { Button } from './Button';
import { cookieConsentManager } from './CookieConsent';

const StyledButton = styled(Button)`
  text-wrap: nowrap;
  padding: ${theme.spacing.c} ${theme.spacing.g};
`;

const PrimaryActionContainer = styled.div`
  padding: 0 ${theme.spacing.g};
`;

interface IProps extends WithTranslation {
  reportRespondent?: NonNullable<ReportSubscriptionSubscription['getReport']['metadata']>['respondent'];
  channel?: NonNullable<GetChannelQuery['channel']> | null;
  showModal?: (modalName: Modals, show: boolean) => (e: React.MouseEvent<HTMLElement> | null) => void;
  user?: IsAccessTokenValidQuery['isAccessTokenValid']['user'];
  t: any;
  someReport?: boolean;
}

const UserMenu = ({ channel, showModal, user, reportRespondent, t, someReport }: IProps) => {
  const history = useHistory();
  const location = useLocation();

  const handleLogout = async () => {
    const shouldRedirectToLogin = await auth.logout();
    if (shouldRedirectToLogin) {
      auth.redirectToLogin();
    }
  };
  const openCookieSettings = () => {
    cookieConsentManager?.showPreferences();
    return false;
  };

  const handleLogin = () => {
    auth.redirectToLogin(channel?.respondent?.contacts?.find((c) => c.type === ContactType.Email)?.value, true);
  };

  const handleOpenMyArnold: () => void = () => {
    if (auth.isLoggedIn()) {
      history.push('/overview');
    } else if (channel && channel.respondent) {
      history.push(`/chat-overview/${channel?.id}`);
    } else {
      history.push('/overview');
    }
  };

  const handleGoToAdmin = () => {
    window.location.href = `${getFrontendConfigValue('SELF_CARE_URL')}/overview`;
  };

  const respondent = channel ? channel.respondent : reportRespondent || null;

  const currentUser = (user && user.respondents![0]) || respondent || null;

  const name = currentUser ? [currentUser.firstname, currentUser.surname].join(' ') : '';

  const isUserOrgAdminOrSysAdmin =
    user &&
    (user.respondents?.find((respondent) => respondent.organizationRole === UserOrgRole.OrgAdmin) ||
      user.systemRole === UserSysRole.SysAdmin);
  const isCurrentRouteOverview =
    location.pathname.includes('/overview') || location.pathname.includes('/chat-overview');
  const isCurrentRouteReport =
    location.pathname.includes('/report') ||
    location.pathname.includes('/results') ||
    location.pathname.includes('/report-sequence') ||
    location.pathname.includes('/process-results');
  const loggedIn = auth.isLoggedIn() ? (respondent ? respondent.user : !!user) : !!user;

  const menuItems = [
    loggedIn &&
      isUserOrgAdminOrSysAdmin && {
        title: t('goToAdminButton'),
        onClick: handleGoToAdmin,
        dataIcom: 'user-menu-open-admin-item',
        element: 'a',
      },
    loggedIn &&
      !isCurrentRouteOverview && {
        title: t('myArnoldButton'),
        onClick: handleOpenMyArnold,
        dataIcom: 'user-menu-open-my-arnold-item',
        element: 'a',
      },
    channel &&
      showModal && {
        title: t('languageChangeButton'),
        onClick: () => showModal(Modals.CHANGE_LANGUAGE, true)(null),
        dataIcom: 'user-menu-change-language-item',
        element: 'button',
      },
    someReport &&
      !isUserOrgAdminOrSysAdmin &&
      !user &&
      !isCurrentRouteOverview && {
        title: t('loginButton'),
        onClick: handleLogin,
        dataIcom: 'user-menu-login-item',
        element: 'a',
      },
    {
      title: t('cookieSettings'),
      onClick: openCookieSettings,
      dataIcom: 'user-menu-cookie-settings-item',
      element: 'button',
    },
    {
      title: t('dsaButton'),
      dataIcom: 'user-menu-dsa-item',
      element: 'a',
      href: 'mailto:help.me@arnold-robot.com',
    },
  ].filter(Boolean) as MenuItems;

  return (
    <UserMenuDropdown
      buttonTitle={
        currentUser && loggedIn
          ? [currentUser.firstname, currentUser.surname].filter(Boolean).map((n) => (n ? n.charAt(0) : null))
          : []
      }
      title={name}
      isLogged={!!loggedIn}
      showMenuHeader={!!loggedIn}
      menuItems={menuItems}
      showFooter={
        !!(someReport || isUserOrgAdminOrSysAdmin || !isCurrentRouteOverview || loggedIn || isCurrentRouteReport)
      }
      showPrimaryActionInFooter={!loggedIn}
      primaryAction={
        <PrimaryActionContainer>
          {isUserOrgAdminOrSysAdmin || isCurrentRouteOverview || isCurrentRouteReport ? (
            <StyledButton onClick={handleLogin} data-icom={'user-menu-login-button'}>
              {t('loginButton')}
            </StyledButton>
          ) : (
            <StyledButton onClick={handleOpenMyArnold} data-icom={'user-menu-open-my-arnold-button'}>
              {t('myArnoldButton')}
            </StyledButton>
          )}
        </PrimaryActionContainer>
      }
      footerMenuItem={{
        title: t('logoutButton'),
        onClick: handleLogout,
        dataIcom: 'user-menu-logout-item',
        element: 'button',
      }}
    />
  );
};

export default withTranslation('userMenu')(UserMenu);
